import {useEffect, useCallback, type RefObject} from 'react'

type DropdownPosition = 'right' | 'offset-left' | 'center' | 'left'

const OFFSET_AMOUNT = 48

function determineDropdownPosition(container: HTMLElement, dropdown: HTMLElement): DropdownPosition {
  const containerRect = container.getBoundingClientRect()
  const dropdownRect = dropdown.getBoundingClientRect()
  const viewportWidth = window.innerWidth - 16

  const fitsRight = containerRect.left + dropdownRect.width <= viewportWidth

  if (fitsRight) {
    return 'right'
  }

  const offsetLeft = containerRect.left - OFFSET_AMOUNT
  const offsetRight = offsetLeft + dropdownRect.width
  const fitsWithOffset = offsetLeft >= 0 && offsetRight <= viewportWidth

  if (fitsWithOffset) {
    return 'offset-left'
  }

  const centerLeft = containerRect.left + containerRect.width / 2 - dropdownRect.width / 2
  const centerRight = centerLeft + dropdownRect.width
  const fitsCenter = centerLeft >= 0 && centerRight <= viewportWidth

  if (fitsCenter) {
    return 'center'
  }

  return 'left'
}

export function useDropdownPositioning(
  containerRef: RefObject<HTMLElement | null>,
  dropdownRef: RefObject<HTMLElement | null>,
) {
  const updateDropdownPosition = useCallback(() => {
    const container = containerRef.current
    const dropdown = dropdownRef.current

    if (!container || !dropdown) return

    if (window.innerWidth < 1012) return

    const position = determineDropdownPosition(container, dropdown)

    dropdown.classList.remove('open-left', 'open-center', 'open-offset-left')

    if (position === 'offset-left') {
      dropdown.classList.add('open-offset-left')
    } else if (position === 'center') {
      dropdown.classList.add('open-center')
    } else if (position === 'left') {
      dropdown.classList.add('open-left')
    }
  }, [containerRef, dropdownRef])

  useEffect(() => {
    const container = containerRef.current
    const dropdown = dropdownRef.current

    if (!container || !dropdown) return

    updateDropdownPosition()

    const resizeObserver = new ResizeObserver(updateDropdownPosition)
    resizeObserver.observe(container)
    resizeObserver.observe(dropdown)

    const mutationObserver = new MutationObserver(() => {
      if (container.classList.contains('open')) {
        updateDropdownPosition()
      }
    })

    mutationObserver.observe(container, {
      attributes: true,
      attributeFilter: ['class'],
    })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [containerRef, dropdownRef, updateDropdownPosition])
}
